import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { departmentsService, DepartmentMember } from '@/services/api/departments';
import { usersService } from '@/services/api/users';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Building2,
  Search,
  MoreHorizontal,
  Trash2,
  Users,
  Plus,
  Loader2,
  UserCircle,
  ArrowLeft,
  Crown,
  UserCog,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export function DepartmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [setHeadDialogOpen, setSetHeadDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<DepartmentMember | null>(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState<'member' | 'manager' | 'head'>('member');

  // Fetch department details
  const { data: department, isLoading: loadingDept } = useQuery({
    queryKey: ['department', id],
    queryFn: () => departmentsService.getDepartment(id!),
    enabled: !!id,
  });

  // Fetch department members
  const { data: membersData, isLoading: loadingMembers } = useQuery({
    queryKey: ['department-members', id],
    queryFn: () => departmentsService.getDepartmentMembers(id!),
    enabled: !!id,
  });

  // Fetch all users for adding members
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersService.listUsers({ limit: 100 }),
  });

  // Filter available users (not already in department)
  const availableUsers = usersData?.users.filter(
    (user) => !membersData?.members.find((m) => m.id === user.id)
  ) ?? [];

  // Add member mutation
  const addMemberMutation = useMutation({
    mutationFn: () =>
      departmentsService.assignUser(id!, { userId: selectedUserId, departmentRole: selectedRole }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['department-members', id] });
      queryClient.invalidateQueries({ queryKey: ['department', id] });
      setAddMemberDialogOpen(false);
      setSelectedUserId('');
      setSelectedRole('member');
    },
  });

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => departmentsService.removeUser(id!, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['department-members', id] });
      queryClient.invalidateQueries({ queryKey: ['department', id] });
      setRemoveDialogOpen(false);
      setSelectedMember(null);
    },
  });

  // Set head mutation
  const setHeadMutation = useMutation({
    mutationFn: (headUserId: string | null) =>
      departmentsService.updateDepartment(id!, { headUserId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['department', id] });
      queryClient.invalidateQueries({ queryKey: ['department-members', id] });
      setSetHeadDialogOpen(false);
      setSelectedMember(null);
    },
  });

  const handleRemoveMember = (member: DepartmentMember) => {
    setSelectedMember(member);
    setRemoveDialogOpen(true);
  };

  const handleSetHead = (member: DepartmentMember) => {
    setSelectedMember(member);
    setSetHeadDialogOpen(true);
  };

  const confirmRemove = () => {
    if (selectedMember) {
      removeMemberMutation.mutate(selectedMember.id);
    }
  };

  const confirmSetHead = () => {
    if (selectedMember) {
      setHeadMutation.mutate(selectedMember.id);
    }
  };

  const getRoleBadge = (role: string, isHead: boolean) => {
    if (isHead) {
      return (
        <Badge className="bg-amber-500">
          <Crown className="mr-1 h-3 w-3" />
          Head
        </Badge>
      );
    }
    switch (role) {
      case 'manager':
        return <Badge variant="secondary">Manager</Badge>;
      default:
        return <Badge variant="outline">Member</Badge>;
    }
  };

  const filteredMembers = membersData?.members.filter(
    (m) =>
      m.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      `${m.firstName} ${m.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loadingDept) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!department) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Department not found</p>
        <Button variant="link" onClick={() => navigate('/organization')}>
          Back to Organization
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <Button
          variant="ghost"
          size="sm"
          className="w-fit"
          onClick={() => navigate('/organization')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Organization
        </Button>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{department.name}</h1>
                {department.code && <Badge variant="outline">{department.code}</Badge>}
              </div>
              {department.description && (
                <p className="text-sm text-muted-foreground">{department.description}</p>
              )}
            </div>
          </div>
          <Button onClick={() => setAddMemberDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Member
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-3 sm:gap-4 grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="p-3 sm:p-6 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Total Members</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
            <div className="text-xl sm:text-2xl font-bold">{department.memberCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-3 sm:p-6 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Department Head</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
            {department.headUserName ? (
              <div className="flex items-center gap-2">
                <UserCircle className="h-5 w-5 text-muted-foreground" />
                <span className="font-medium">{department.headUserName}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">Not assigned</span>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-3 sm:p-6 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
            <Badge variant={department.isActive ? 'default' : 'secondary'}>
              {department.isActive ? 'Active' : 'Inactive'}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="p-3 sm:p-6 pb-2">
            <CardTitle className="text-xs sm:text-sm font-medium">Created</CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0 sm:p-6 sm:pt-0">
            <p className="text-sm text-muted-foreground">
              {formatDistanceToNow(new Date(department.createdAt), { addSuffix: true })}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Members List */}
      <Card>
        <CardHeader className="p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Members</CardTitle>
              <CardDescription>People in this department</CardDescription>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search members..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loadingMembers ? (
            <div className="p-4 space-y-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredMembers?.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No members yet</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Add members to this department
              </p>
              <Button className="mt-4" onClick={() => setAddMemberDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Member
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="hidden sm:table-cell">Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="hidden md:table-cell">Status</TableHead>
                    <TableHead className="hidden lg:table-cell">Joined</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMembers?.map((member) => {
                    // Check both headUserId match and departmentRole for head identification
                    const isHead = department.headUserId === member.id || member.departmentRole === 'head';
                    return (
                      <TableRow key={member.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <UserCircle className="h-8 w-8 text-muted-foreground" />
                            <div>
                              <p className="font-medium">
                                {member.firstName} {member.lastName}
                              </p>
                              <p className="text-xs text-muted-foreground sm:hidden">
                                {member.email}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          {member.email}
                        </TableCell>
                        <TableCell>
                          {getRoleBadge(member.departmentRole, isHead)}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <Badge
                            variant={member.status === 'active' ? 'default' : 'secondary'}
                          >
                            {member.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-muted-foreground text-sm">
                          {formatDistanceToNow(new Date(member.joinedAt), { addSuffix: true })}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {!isHead && (
                                <DropdownMenuItem onClick={() => handleSetHead(member)}>
                                  <Crown className="mr-2 h-4 w-4" />
                                  Set as Head
                                </DropdownMenuItem>
                              )}
                              {isHead && (
                                <DropdownMenuItem onClick={() => setHeadMutation.mutate(null)}>
                                  <Crown className="mr-2 h-4 w-4 text-muted-foreground" />
                                  Remove as Head
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem asChild>
                                <Link to={`/users/${member.id}`}>
                                  <UserCog className="mr-2 h-4 w-4" />
                                  View Profile
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleRemoveMember(member)}
                                className="text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Member Dialog */}
      <Dialog open={addMemberDialogOpen} onOpenChange={setAddMemberDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Member</DialogTitle>
            <DialogDescription>
              Add a user to this department
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select User</Label>
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a user..." />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">
                      No available users
                    </div>
                  ) : (
                    availableUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.firstName} {user.lastName} ({user.email})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Role in Department</Label>
              <Select value={selectedRole} onValueChange={(v) => setSelectedRole(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="head">Head</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMemberDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => addMemberMutation.mutate()}
              disabled={!selectedUserId || addMemberMutation.isPending}
            >
              {addMemberMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Member Dialog */}
      <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {selectedMember?.firstName} {selectedMember?.lastName}{' '}
              from this department?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removeMemberMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Set Head Dialog */}
      <AlertDialog open={setHeadDialogOpen} onOpenChange={setSetHeadDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Set Department Head</AlertDialogTitle>
            <AlertDialogDescription>
              Set {selectedMember?.firstName} {selectedMember?.lastName} as the head of this
              department? This will replace the current head if one exists.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSetHead}>
              {setHeadMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Crown className="mr-2 h-4 w-4" />
              )}
              Set as Head
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
